import ms from "milsymbol";

import { unitDesignation } from "../../i18n/designation";

/** Symboles APP-6D dessinés à partir du code SIDC calculé par le serveur (même principe que Prevail :
 *  le symbole n'est pas une image choisie par nom, il découle du typage de l'unité). */

// Taille milsymbol donnant, comme sur l'iPad, un cadre d'environ 49 px de large à tout zoom : rectangle ami
// (1,5 × taille) et losange ennemi (1,44 × taille). Voir MapLibreMapView.unitIconScale.
const FRIEND_SIZE = 33;
const OTHER_SIZE = 34;
export const SYMBOL_PREFIX = "sidc:";

/** Bleu des unités amies de l'iPad (couleur « blueGenerex », #5CBDFF) : fond des symboles amis, et couleur
 *  des limites du fuseau et des LIMA. */
export const FRIEND_BLUE = "#5CBDFF";
/** Rouge des unités ennemies de l'iPad (fond mesuré sur ses images, #DC7B7B). */
export const HOSTILE_RED = "#DC7B7B";

const OPTIONS = {
  outlineWidth: 3,
  outlineColor: "rgba(255,255,255,0.9)",
};

/** Options du symbole : fonds de l'iPad (identité APP-6D, 4e chiffre du SIDC : 2-3 ami, 5-6 ennemi). */
function options(sidc: string, size?: number) {
  const identity = sidc[3];
  if (identity === "3" || identity === "2") return { ...OPTIONS, size: size ?? FRIEND_SIZE, fillColor: FRIEND_BLUE };
  if (identity === "6" || identity === "5") return { ...OPTIONS, size: size ?? OTHER_SIZE, fillColor: HOSTILE_RED };
  return { ...OPTIONS, size: size ?? OTHER_SIZE };
}

/** SVG du symbole, pour les fiches et les listes. `amplifiers` : numéros portés en bas à gauche (désignation
 *  unique) et en bas à droite (formation supérieure), pour les aperçus de l'éditeur d'ordre de bataille — la carte
 *  passe par `symbolImage`, inchangée. */
export function symbolSvg(sidc: string, size = 34, amplifiers?: { left?: string | null; right?: string | null }): string {
  if (!amplifiers?.left && !amplifiers?.right) return new ms.Symbol(sidc, options(sidc, size)).asSVG();
  return new ms.Symbol(sidc, {
    ...options(sidc, size),
    uniqueDesignation: amplifiers.left ?? "",
    higherFormation: amplifiers.right ?? "",
  }).asSVG();
}

/** Identifiant d'image d'une unité sur la carte : SIDC et désignations portées par le symbole (unité en bas à
 *  gauche, formation supérieure en bas à droite), comme les images de l'iPad. */
export function symbolId(sidc: string, left?: string | null, right?: string | null): string {
  return left || right ? `${SYMBOL_PREFIX}${sidc}|${left ?? ""}|${right ?? ""}` : `${SYMBOL_PREFIX}${sidc}`;
}

/** Image du symbole pour la carte, recentrée sur son point d'ancrage (le centre du cadre) :
 *  l'échelon et les désignations dessinés autour du cadre ne doivent pas décaler l'unité. */
export function symbolImage(sidc: string, pixelRatio: number, left = "", right = ""): ImageData {
  // Interface en anglais : désignations OTAN dessinées (« 1erRCP » → « 1 Para Inf Regt »), l'identifiant de
  // l'image gardant les désignations françaises (clés).
  const shown = { uniqueDesignation: unitDesignation(left), higherFormation: unitDesignation(right) };
  const symbol = new ms.Symbol(sidc, { ...options(sidc), ...shown });
  const source = symbol.asCanvas(pixelRatio);
  const anchor = symbol.getAnchor();
  const ax = anchor.x * pixelRatio;
  const ay = anchor.y * pixelRatio;
  const width = Math.ceil(2 * Math.max(ax, source.width - ax));
  const height = Math.ceil(2 * Math.max(ay, source.height - ay));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponible");
  ctx.drawImage(source, Math.round(width / 2 - ax), Math.round(height / 2 - ay));
  return ctx.getImageData(0, 0, width, height);
}

interface ImageHost {
  hasImage(id: string): boolean;
  addImage(id: string, image: ImageData, options?: { pixelRatio?: number }): void;
}

/** Ajoute à la carte, à la demande, l'image d'un symbole référencé par « sidc:<code> ». */
export function provideSymbolImage(map: ImageHost, id: string): void {
  if (!id.startsWith(SYMBOL_PREFIX) || map.hasImage(id)) return;
  const pixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
  const [sidc = "", left = "", right = ""] = id.slice(SYMBOL_PREFIX.length).split("|");
  map.addImage(id, symbolImage(sidc, pixelRatio, left, right), { pixelRatio });
}
